import React, { useEffect, useState } from "react";
import { Button } from "@heathmont/moon-core-tw";
import { ControlsPlus } from "@heathmont/moon-icons-tw";
import PageTitle from "../../Components/layouts/header/page-title";
import LeaveDrawer from "./widgets/LeaveDrawer";
import { useSelector } from "react-redux";
import { RootState, store } from "../../store/store";
import { getMyLeaves, createLeaveRequest } from "../../services/employee.service";
import {
  setSnackBar,
  setToastMessage,
} from "../../features/ToastSlice/toastSlice";
import moment from "moment";
import { notificationSocket } from "../../utils/notification.socket.service";
import { apiBaseUrl } from "../../constants/environmentConstants/environmentConstants";

const LEAVE_TYPE_LABELS: Record<string, string> = {
  AL: "Annual Leave",
  SL: "Sick Leave",
  CO: "Compensatory Off",
  ML: "Maternity Off",
  LL: "Learning Leave",
  CL: "Condolence Leave",
  PL: "Paternity Leave",
  UL: "Unpaid Leave",
};

const STATUS_CLASSES: Record<string, string> = {
  Pending:  "bg-yellow-100 text-yellow-700",
  Accepted: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700",
};

const Leaves = () => {
  const { User } = useSelector((state: RootState) => state.auth);
  const currentYear = Number(moment().format("YYYY"));

  const [year, setYear] = useState<number>(currentYear);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const fetchLeaves = async () => {
    setIsLoading(true);
    const res: any = await getMyLeaves(year);
    if (res?.res) {
      // Sort newest first
      const sorted = [...res.res].sort(
        (a: any, b: any) =>
          moment(b.from?.date, "MM/DD/YYYY").valueOf() -
          moment(a.from?.date, "MM/DD/YYYY").valueOf()
      );
      setLeaves(sorted);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchLeaves();
  }, [year]);

  // Re-fetch when manager accepts or declines this nurse's leave request
  useEffect(() => {
    if (!User?.id || !apiBaseUrl) return;

    // Ensure socket is initialized
    if (!notificationSocket.socket?.connected) {
      notificationSocket.initializeConnection(apiBaseUrl, User.id, User.role);
    }

    const handler = () => {
      console.log("[Leaves] leaveResponse received — refreshing");
      fetchLeaves();
    };

    // Retry attaching the listener until socket is ready
    let retries = 0;
    const attach = () => {
      if (notificationSocket.socket) {
        notificationSocket.socket.off("leaveResponse", handler);
        notificationSocket.socket.on("leaveResponse", handler);
        console.log("[Leaves] leaveResponse listener attached");
      } else if (retries < 10) {
        retries++;
        setTimeout(attach, 300);
      }
    };
    attach();

    return () => {
      notificationSocket?.socket?.off("leaveResponse", handler);
    };
  }, [User?.id, year]);

  const handleSubmit = async (
    leaveType: string,
    fromDate: string,
    toDate: string,
    reason: string
  ) => {
    const from = {
      date: moment(fromDate).format("MM/DD/YYYY"),
      dayNumber: moment(fromDate).date(),
      month: moment(fromDate).month() + 1,
      year: moment(fromDate).year(),
    };
    const to = {
      date: moment(toDate).format("MM/DD/YYYY"),
      dayNumber: moment(toDate).date(),
      month: moment(toDate).month() + 1,
      year: moment(toDate).year(),
    };

    const res: any = await createLeaveRequest({
      employeeId: User?.employeeId,
      employeeName: User?.employeeName,
      leaveType,
      from,
      to,
    });

    if (res?.res) {
      store.dispatch(setSnackBar("success"));
      store.dispatch(setToastMessage("Leave request submitted successfully"));
      setIsDrawerOpen(false);
      fetchLeaves();
    } else {
      store.dispatch(setSnackBar("error"));
      store.dispatch(
        setToastMessage(res?.err?.message || "Failed to submit leave request")
      );
    }
  };

  return (
    <>
      <div className="bg-[#f8f8f8] min-h-screen">
        <PageTitle
          title="Leaves"
          subtitle="Apply and view your leave requests"
        />

        <div className="p-[20px_30px]">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-6">
            {/* Year selector */}
            <div className="flex items-center gap-2">
              {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  className={`px-4 py-1.5 rounded-full text-moon-14 font-medium border transition-colors ${
                    year === y
                      ? "bg-frieza text-white border-frieza"
                      : "bg-white text-gray-600 border-beerus hover:border-frieza"
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>

            {/* Apply button */}
            <Button
              variant="tertiary"
              onClick={() => setIsDrawerOpen(true)}
            >
              <ControlsPlus className="text-moon-24" />
              Apply Leave
            </Button>
          </div>

          {/* Leave list */}
          {isLoading ? (
            <div className="flex justify-center items-center h-48 text-gray-400 text-moon-14">
              Loading...
            </div>
          ) : leaves.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <img
                src={"images/no-schedule-to-approve.svg"}
                alt="No leaves"
                className="mb-4"
              />
              <p className="text-gray-400 text-moon-14">No leave requests for {year}</p>
            </div>
          ) : (
            <div className="bg-white rounded-moon-s-lg shadow-sm overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-5 gap-4 px-6 py-3 bg-gray-50 border-b border-beerus text-moon-12 font-semibold text-gray-500 uppercase tracking-wide">
                <div>Leave Type</div>
                <div>From</div>
                <div>To</div>
                <div>Duration</div>
                <div>Status</div>
              </div>

              {/* Rows */}
              {leaves.map((leave: any) => {
                const fromMoment = moment(leave.from?.date, "MM/DD/YYYY");
                const toMoment = moment(leave.to?.date, "MM/DD/YYYY");
                const duration = toMoment.diff(fromMoment, "days") + 1;

                return (
                  <div
                    key={leave._id}
                    className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-beerus hover:bg-gray-50 transition-colors items-center"
                  >
                    <div className="text-moon-14 font-medium text-blackcst">
                      {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                    </div>
                    <div className="text-moon-14 text-gray-600">
                      {fromMoment.format("DD MMM YYYY")}
                    </div>
                    <div className="text-moon-14 text-gray-600">
                      {toMoment.format("DD MMM YYYY")}
                    </div>
                    <div className="text-moon-14 text-gray-600">
                      {duration} day{duration !== 1 ? "s" : ""}
                    </div>
                    <div>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-moon-12 font-medium ${
                          STATUS_CLASSES[leave.status] ||
                          "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {leave.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      <LeaveDrawer
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        handleClose={() => setIsDrawerOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default Leaves;
